import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { InvalidFieldsContext } from 'components/common-form/contexts/InvalidFieldsContext';
import { PatchedFieldsContext } from 'components/common-form/contexts/PatchedFieldsContext';
import { useContext, useMemo } from 'react';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import type { InvalidFields } from 'state/selectors/meta/static/utils';
import { areLegacyInvalidFields } from 'state/selectors/meta/static/utils';

import { getFirstVisibleValidationError } from '../useFieldValidationError';

function hasValidationError(invalidFields: InvalidFields, fieldId: string): boolean {
  if (areLegacyInvalidFields(invalidFields)) {
    return false;
  }
  return getFirstVisibleValidationError(invalidFields[fieldId]) !== undefined;
}

const PRODUCT_CODES_CELL_KEY_PREFIX_SEPARATOR = '::';
const PRODUCT_CODE_VISIBLE_COLUMN_IDS = ['ProductCodeValue', 'ProductCodeIsRequested'] as const;
const PRODUCT_CODE_TERM_SHEET_COLUMN_ID = 'ProductCodeDisplayInTS';

function countProductCodesRows(
  fieldId: string,
  patchedFields: readonly string[],
  isTermsheetVisible: boolean,
  rowCount: number,
): number {
  const validColumnIds = new Set<string>(PRODUCT_CODE_VISIBLE_COLUMN_IDS);
  if (isTermsheetVisible) {
    validColumnIds.add(PRODUCT_CODE_TERM_SHEET_COLUMN_ID);
  }

  const rowIndexes = new Set<string>();
  const prefix = `${fieldId}${PRODUCT_CODES_CELL_KEY_PREFIX_SEPARATOR}`;
  for (const patchedField of patchedFields) {
    if (!patchedField.startsWith(prefix)) {
      continue;
    }

    const [rowIndex, columnId] = patchedField.slice(prefix.length).split(PRODUCT_CODES_CELL_KEY_PREFIX_SEPARATOR);
    const rowNumber = Number(rowIndex);
    if (
      rowIndex !== undefined &&
      Number.isInteger(rowNumber) &&
      rowNumber >= 0 &&
      rowNumber < rowCount &&
      validColumnIds.has(columnId ?? '')
    ) {
      rowIndexes.add(rowIndex);
    }
  }

  return rowIndexes.size;
}

function countPatchedFieldsForId(
  fieldId: string,
  patchedFields: readonly string[],
  metadataById: FieldMetadataById,
): number {
  const metadata = metadataById[fieldId];
  if (metadata?.type === 'ProductCodesGrid') {
    return countProductCodesRows(
      fieldId,
      patchedFields,
      metadata.isDisplayTermsheetColumnVisible,
      metadata.rows.length,
    );
  }

  return patchedFields.some(id => id === fieldId || id.startsWith(`${fieldId}::`)) ? 1 : 0;
}

/**
 * Counts, per block, the number of fields that currently resolve to the modified visual highlight.
 * Mirrors {@link useCountInvalidFields} so the tab badge / side-panel message stay consistent with
 * the single-field {@link usePatchedFieldHighlight} suppression rules: a field is counted only when it
 * is patched, not suppressed by a validation error or `metadata.isHighlighted`, and not hidden.
 */
export function useCountPatchedFields(blocks: readonly (readonly string[])[]): number[] {
  const { fields } = useContext(PatchedFieldsContext);
  const invalidFields = useContext(InvalidFieldsContext);
  const metadataById = useContext(FieldMetadataContext);

  return useMemo(() => {
    return blocks.map(fieldIds =>
      fields.reduce((count, patchedField) => {
        const match = fieldIds
          .map(fieldId => ({
            fieldId,
            patchedFieldCount: countPatchedFieldsForId(fieldId, [patchedField], metadataById),
          }))
          .find(({ patchedFieldCount }) => patchedFieldCount > 0);

        if (!match) {
          return count;
        }
        const { fieldId, patchedFieldCount } = match;

        if (hasValidationError(invalidFields, fieldId)) {
          return count;
        }
        const metadata = metadataById[fieldId];
        if (metadata?.isHighlighted || metadata?.isHidden) {
          return count;
        }
        return count + patchedFieldCount;
      }, 0),
    );
  }, [blocks, fields, invalidFields, metadataById]);
}
